import React, { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { GlobalSet } from "../../global/GlobalProvider";
import PageTitle from "../../../layouts/PageTitle";
import { useTranslation } from "react-i18next";
import profile from "../../../../images/profile/profile.png";

/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
  "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

/* =========================================================
   HELPERS
========================================================= */

const safeValue = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  return value;
};

const formatMoney = (value, symbol = "$") => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return `${symbol}0`;
  }

  return `${symbol}${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number)}`;
};

const StatIcon = ({ type }) => {
  const icons = {
    balance: "◈",
    deposit: "↓",
    income: "↗",
    withdraw: "↑",
    bonus: "★",
    affiliate: "◎",
  };

  return <span>{icons[type] || "•"}</span>;
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const AppProfile = () => {
  const { CurrencySymbol } = GlobalSet();
  const { t } = useTranslation();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  /* =========================================================
     LOAD PROFILE
  ========================================================= */

  useEffect(() => {
    let mounted = true;

    const fetchProfile = async () => {
      const authToken = localStorage.getItem("auth_token");
      const selectedCurrency =
        localStorage.getItem("selectedCurrency") || "";

      if (!authToken) {
        if (mounted) {
          setPageError("Your session has expired. Please login again.");
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setPageError("");

        const response = await axios.post("/apps-profile", {
          key_s: API_KEY,
          currency: selectedCurrency,
        });

        if (!mounted) return;

        const responseData = response?.data;

        /*
         * Your old component reads response.data.data.
         * This keeps the same backend response structure.
         */
        if (responseData?.data) {
          setUser(responseData.data);
        } else {
          setUser(null);
          setPageError("Profile information was not found.");
        }
      } catch (error) {
        console.error("Profile load error:", error);

        if (!mounted) return;

        setPageError(
          error?.response?.data?.message ||
            "Unable to load your profile. Please try again."
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      mounted = false;
    };
  }, []);

  /* =========================================================
     PROFILE DATA
  ========================================================= */

  const currency =
    CurrencySymbol ||
    localStorage.getItem("selectedCurrency") ||
    "৳";

  const fullName = useMemo(() => {
    if (!user) return "";

    return [user.name, user.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
  }, [user]);

  const stats = useMemo(() => {
    if (!user) return [];

    return [
      {
        key: "balance",
        title: t("pro_pg_curr_bal"),
        fallbackTitle: "Current Balance",
        value: user.balance,
        icon: "balance",
        note: "Available balance",
      },
      {
        key: "deposit",
        title: t("pro_pg_tot_dep"),
        fallbackTitle: "Total Deposit",
        value: user.total_deposit,
        icon: "deposit",
        note: "Lifetime deposits",
      },
      {
        key: "income",
        title: t("pro_pg_tot_inc"),
        fallbackTitle: "Total Income",
        value: user.total_Income,
        icon: "income",
        note: "Lifetime earnings",
      },
      {
        key: "withdraw",
        title: t("pro_pg_tot_wit"),
        fallbackTitle: "Total Withdraw",
        value: user.total_withdraw,
        icon: "withdraw",
        note: "Lifetime withdrawals",
      },
    ];
  }, [user, t]);

  /* =========================================================
     LOADING
  ========================================================= */

  /* Loading is shown only inside the profile data areas. */

  /* =========================================================
     ERROR
  ========================================================= */

  if (!loading && (pageError || !user)) {
    return (
      <Fragment>
        <ProfileStyles />

        <div className="sp-page">
          <div className="sp-container">
            <div className="sp-error-card">
              <div className="sp-error-icon">!</div>
              <h3>Profile Unavailable</h3>
              <p>{pageError || "Unable to load profile information."}</p>

              <button
                type="button"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  /* =========================================================
     VIEW
  ========================================================= */

  return (
    <Fragment>
      <ProfileStyles />

      <div className="sp-page">
        <div className="sp-container">

          {/* =================================================
              PROFILE HERO
          ================================================= */}

          <section className="sp-profile-hero">
            <div className="sp-cover-decoration sp-cover-one"></div>
            <div className="sp-cover-decoration sp-cover-two"></div>

            <div className="sp-profile-main">
              <div className="sp-avatar-wrap">
                <img
                  src={profile}
                  alt={fullName || "Profile"}
                  className="sp-avatar"
                />
                <span className="sp-online-dot"></span>
              </div>

              <div className="sp-profile-copy">
                <span className="sp-account-label">
                  My Account
                </span>

                {loading ? (
                  <div className="sp-hero-data-skeleton">
                    <span className="sp-skeleton-line sp-sk-hero-name"></span>
                    <span className="sp-skeleton-line sp-sk-hero-email"></span>
                    <div className="sp-sk-badge-row">
                      <span className="sp-skeleton-line sp-sk-badge"></span>
                      <span className="sp-skeleton-line sp-sk-badge"></span>
                    </div>
                  </div>
                ) : (
                  <Fragment>
                    <h1>{safeValue(fullName, "Account User")}</h1>

                    <p>{safeValue(user?.email)}</p>

                    <div className="sp-profile-badges">
                      <span>
                        <small>Reference ID</small>
                        {safeValue(user?.reference_code)}
                      </span>

                      {user?.mobile_No && (
                        <span>
                          <small>Mobile</small>
                          {user?.mobile_No}
                        </span>
                      )}
                    </div>
                  </Fragment>
                )}
              </div>
            </div>

            <div className="sp-account-status">
              <span className="sp-status-dot"></span>

              <div>
                <small>Account Status</small>
                <strong>Active</strong>
              </div>
            </div>
          </section>

          {/* =================================================
              STATISTICS
          ================================================= */}

          <section className="sp-stat-grid">
            {loading
              ? Array.from({ length: 4 }).map((_, index) => (
                  <article
                    className="sp-stat-card sp-data-skeleton"
                    key={`stat-skeleton-${index}`}
                  >
                    <div className="sp-stat-top">
                      <span className="sp-skeleton-box sp-sk-stat-icon"></span>
                      <span className="sp-skeleton-line sp-sk-chip"></span>
                    </div>
                    <span className="sp-skeleton-line sp-sk-stat-value"></span>
                    <span className="sp-skeleton-line sp-sk-stat-title"></span>
                    <span className="sp-skeleton-line sp-sk-stat-note"></span>
                  </article>
                ))
              : stats.map((item) => {
                  const translatedTitle =
                    item.title && item.title !== item.key
                      ? item.title
                      : item.fallbackTitle;

                  return (
                    <article
                      className={`sp-stat-card sp-${item.key}`}
                      key={item.key}
                    >
                      <div className="sp-stat-top">
                        <div className="sp-stat-icon">
                          <StatIcon type={item.icon} />
                        </div>

                        <span className="sp-stat-chip">
                          Account
                        </span>
                      </div>

                      <div className="sp-stat-value">
                        {formatMoney(item.value, currency)}
                      </div>

                      <h3>{translatedTitle}</h3>
                      <p>{item.note}</p>
                    </article>
                  );
                })}
          </section>

          {/* =================================================
              CONTENT
          ================================================= */}

          <div className="sp-content-grid">

            {/* ===============================================
                PERSONAL INFORMATION
            =============================================== */}

            <section className="sp-panel">
              <div className="sp-panel-header">
                <div>
                  <span className="sp-panel-kicker">
                    Profile
                  </span>
                  <h2>Personal Information</h2>
                  <p>
                    Your basic account and contact information.
                  </p>
                </div>

                <div className="sp-panel-icon">◎</div>
              </div>

              <div className="sp-info-list-wrap">
                {loading && (
                  <div className="sp-panel-local-loading">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <div className="sp-sk-info-row" key={`info-${index}`}>
                        <span className="sp-skeleton-box sp-sk-info-icon"></span>
                        <div>
                          <span className="sp-skeleton-line sp-sk-info-label"></span>
                          <span className="sp-skeleton-line sp-sk-info-value"></span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className={`sp-info-list ${loading ? "sp-data-hidden" : ""}`}>
                <div className="sp-info-row">
                  <div className="sp-info-icon">N</div>

                  <div className="sp-info-copy">
                    <span>{t("pro_pg_name")}</span>
                    <strong>{safeValue(user?.name)}</strong>
                  </div>
                </div>

                <div className="sp-info-row">
                  <div className="sp-info-icon">L</div>

                  <div className="sp-info-copy">
                    <span>Last Name</span>
                    <strong>{safeValue(user?.last_name)}</strong>
                  </div>
                </div>

                <div className="sp-info-row">
                  <div className="sp-info-icon">@</div>

                  <div className="sp-info-copy">
                    <span>{t("pro_pg_email")}</span>
                    <strong className="sp-break">
                      {safeValue(user?.email)}
                    </strong>
                  </div>
                </div>

                <div className="sp-info-row">
                  <div className="sp-info-icon">⌖</div>

                  <div className="sp-info-copy">
                    <span>{t("pro_pg_location")}</span>
                    <strong>{safeValue(user?.address)}</strong>
                  </div>
                </div>

                {user?.mobile_No && (
                  <div className="sp-info-row">
                    <div className="sp-info-icon">☎</div>

                    <div className="sp-info-copy">
                      <span>Mobile Number</span>
                      <strong>{user?.mobile_No}</strong>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </section>

            {/* ===============================================
                ACCOUNT OVERVIEW
            =============================================== */}

            <aside className="sp-panel sp-overview-panel">
              <div className="sp-panel-header">
                <div>
                  <span className="sp-panel-kicker">
                    Overview
                  </span>
                  <h2>Account Summary</h2>
                  <p>
                    A quick summary of your account.
                  </p>
                </div>
              </div>

              {loading ? (
                <div className="sp-overview-local-skeleton">
                  <div className="sp-sk-overview-profile">
                    <span className="sp-skeleton-box sp-sk-avatar"></span>
                    <div>
                      <span className="sp-skeleton-line sp-sk-overview-name"></span>
                      <span className="sp-skeleton-line sp-sk-overview-ref"></span>
                    </div>
                  </div>

                  <div className="sp-sk-overview-list">
                    {Array.from({ length: 4 }).map((_, index) => (
                      <div key={`overview-${index}`}>
                        <span className="sp-skeleton-line sp-sk-overview-label"></span>
                        <span className="sp-skeleton-line sp-sk-overview-value"></span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Fragment>
                  <div className="sp-overview-profile">
                    <img src={profile} alt="Profile" />

                    <div>
                      <strong>{safeValue(fullName, "Account User")}</strong>
                      <span>{safeValue(user?.reference_code)}</span>
                    </div>
                  </div>

                  <div className="sp-overview-list">
                    <div>
                      <span>Current Balance</span>
                      <strong>
                        {formatMoney(user?.balance, currency)}
                      </strong>
                    </div>

                    <div>
                      <span>Bonus Balance</span>
                      <strong>
                        {formatMoney(user?.bonus_balance, currency)}
                      </strong>
                    </div>

                    <div>
                      <span>Affiliate Earning</span>
                      <strong>
                        {formatMoney(user?.affiliate_earning, currency)}
                      </strong>
                    </div>

                    <div>
                      <span>Reference Code</span>
                      <strong>
                        {safeValue(user?.reference_code)}
                      </strong>
                    </div>
                  </div>
                </Fragment>
              )}

              <div className="sp-security-note">
                <span>✓</span>

                <p>
                  Keep your account information secure and never share
                  sensitive login information with anyone.
                </p>
              </div>
            </aside>

          </div>
        </div>
      </div>
    </Fragment>
  );
};

/* =========================================================
   STYLES
========================================================= */

const ProfileStyles = () => (
  <style>{`
    .sp-page {
      --sp-primary: #635bff;
      --sp-primary-dark: #5048d9;
      --sp-text: #182230;
      --sp-muted: #667085;
      --sp-border: #e8eaf0;
      --sp-bg: #f7f8fb;
      --sp-green: #12b76a;

      min-height: 100vh;
      padding: 4px 0 28px;
      color: var(--sp-text);
      font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }

    .sp-container {
      width: 100%;
      margin: 0 auto;
    }

    /* HERO */

    .sp-profile-hero {
      position: relative;
      overflow: hidden;
      min-height: 220px;
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 25px;
      padding: 35px;
      margin-bottom: 20px;
      border-radius: 24px;
      background:
        linear-gradient(
          135deg,
          #171b2d 0%,
          #252b48 55%,
          #393568 100%
        );
      box-shadow: 0 18px 50px rgba(31, 38, 65, 0.12);
    }

    .sp-cover-decoration {
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
    }

    .sp-cover-one {
      width: 330px;
      height: 330px;
      right: -100px;
      top: -190px;
      background: rgba(255, 255, 255, 0.07);
    }

    .sp-cover-two {
      width: 180px;
      height: 180px;
      right: 180px;
      bottom: -125px;
      background: rgba(99, 91, 255, 0.18);
    }

    .sp-profile-main {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      gap: 20px;
    }

    .sp-avatar-wrap {
      position: relative;
      flex: 0 0 105px;
      width: 105px;
      height: 105px;
      padding: 5px;
      border-radius: 28px;
      background: rgba(255, 255, 255, 0.13);
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
    }

    .sp-avatar {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      border: 3px solid #fff;
      border-radius: 23px;
      background: #fff;
    }

    .sp-online-dot {
      position: absolute;
      right: 1px;
      bottom: 7px;
      width: 17px;
      height: 17px;
      border: 4px solid #22273f;
      border-radius: 50%;
      background: var(--sp-green);
    }

    .sp-account-label {
      display: inline-block;
      margin-bottom: 7px;
      color: #cbc8ff;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.7px;
      text-transform: uppercase;
    }

    .sp-profile-copy h1 {
      margin: 0 0 5px;
      color: #fff;
      font-size: 27px;
      font-weight: 850;
      line-height: 1.2;
    }

    .sp-profile-copy > p {
      margin: 0;
      color: rgba(255, 255, 255, 0.62);
      font-size: 12px;
    }

    .sp-profile-badges {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 13px;
    }

    .sp-profile-badges span {
      display: flex;
      align-items: center;
      gap: 7px;
      padding: 7px 10px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 9px;
      background: rgba(255, 255, 255, 0.07);
      color: #fff;
      font-size: 10px;
      font-weight: 750;
    }

    .sp-profile-badges small {
      color: rgba(255, 255, 255, 0.48);
      font-size: 8px;
      font-weight: 600;
    }

    .sp-account-status {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 160px;
      padding: 13px 15px;
      border: 1px solid rgba(255, 255, 255, 0.11);
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.07);
      backdrop-filter: blur(8px);
    }

    .sp-status-dot {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: #75e5aa;
      box-shadow: 0 0 0 5px rgba(117, 229, 170, 0.12);
    }

    .sp-account-status small {
      display: block;
      margin-bottom: 1px;
      color: rgba(255, 255, 255, 0.5);
      font-size: 8px;
    }

    .sp-account-status strong {
      display: block;
      color: #fff;
      font-size: 11px;
      font-weight: 800;
    }

    /* STATS */

    .sp-stat-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 14px;
      margin-bottom: 20px;
    }

    .sp-stat-card {
      position: relative;
      overflow: hidden;
      min-height: 165px;
      padding: 18px;
      border: 1px solid var(--sp-border);
      border-radius: 18px;
      background: #fff;
      box-shadow: 0 7px 24px rgba(30, 38, 64, 0.045);
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .sp-stat-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 30px rgba(30, 38, 64, 0.08);
    }

    .sp-stat-card::after {
      content: "";
      position: absolute;
      width: 95px;
      height: 95px;
      right: -38px;
      bottom: -48px;
      border-radius: 50%;
      background: rgba(99, 91, 255, 0.05);
    }

    .sp-stat-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 16px;
    }

    .sp-stat-icon {
      width: 39px;
      height: 39px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      background: #efedff;
      color: var(--sp-primary);
      font-size: 17px;
      font-weight: 900;
    }

    .sp-deposit .sp-stat-icon {
      background: #ecfdf3;
      color: #079455;
    }

    .sp-income .sp-stat-icon {
      background: #eef4ff;
      color: #2970ff;
    }

    .sp-withdraw .sp-stat-icon {
      background: #fff4ed;
      color: #e04f16;
    }

    .sp-stat-chip {
      padding: 4px 8px;
      border-radius: 20px;
      background: #f5f6f8;
      color: #98a2b3;
      font-size: 8px;
      font-weight: 750;
      text-transform: uppercase;
    }

    .sp-stat-value {
      margin-bottom: 5px;
      color: var(--sp-text);
      font-size: 22px;
      font-weight: 900;
      line-height: 1.2;
      word-break: break-word;
    }

    .sp-stat-card h3 {
      margin: 0 0 3px;
      color: #344054;
      font-size: 11px;
      font-weight: 800;
    }

    .sp-stat-card p {
      margin: 0;
      color: #98a2b3;
      font-size: 9px;
    }

    /* CONTENT */

    .sp-content-grid {
      display: grid;
      grid-template-columns: minmax(0, 1.5fr) minmax(300px, 0.7fr);
      gap: 20px;
      align-items: start;
    }

    .sp-panel {
      padding: 22px;
      border: 1px solid var(--sp-border);
      border-radius: 20px;
      background: #fff;
      box-shadow: 0 8px 30px rgba(30, 38, 64, 0.045);
    }

    .sp-panel-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 15px;
      padding-bottom: 17px;
      margin-bottom: 4px;
      border-bottom: 1px solid #eef0f3;
    }

    .sp-panel-kicker {
      display: block;
      margin-bottom: 3px;
      color: var(--sp-primary);
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.6px;
      text-transform: uppercase;
    }

    .sp-panel-header h2 {
      margin: 0 0 4px;
      color: var(--sp-text);
      font-size: 17px;
      font-weight: 850;
    }

    .sp-panel-header p {
      margin: 0;
      color: var(--sp-muted);
      font-size: 10px;
      line-height: 1.5;
    }

    .sp-panel-icon {
      width: 38px;
      height: 38px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 11px;
      background: #efedff;
      color: var(--sp-primary);
      font-size: 16px;
      font-weight: 900;
    }

    /* INFO */

    .sp-info-list {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 18px;
    }

    .sp-info-row {
      display: flex;
      align-items: center;
      gap: 11px;
      min-width: 0;
      padding: 16px 2px;
      border-bottom: 1px solid #f0f1f4;
    }

    .sp-info-icon {
      flex: 0 0 35px;
      width: 35px;
      height: 35px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      background: #f5f4ff;
      color: var(--sp-primary);
      font-size: 11px;
      font-weight: 900;
    }

    .sp-info-copy {
      min-width: 0;
    }

    .sp-info-copy span {
      display: block;
      margin-bottom: 2px;
      color: #98a2b3;
      font-size: 9px;
    }

    .sp-info-copy strong {
      display: block;
      color: #344054;
      font-size: 11px;
      font-weight: 800;
    }

    .sp-break {
      overflow-wrap: anywhere;
    }

    /* OVERVIEW */

    .sp-overview-panel {
      position: sticky;
      top: 20px;
    }

    .sp-overview-profile {
      display: flex;
      align-items: center;
      gap: 11px;
      padding: 14px 0;
      border-bottom: 1px solid #eef0f3;
    }

    .sp-overview-profile img {
      width: 42px;
      height: 42px;
      object-fit: cover;
      border: 2px solid #eeecff;
      border-radius: 12px;
    }

    .sp-overview-profile strong {
      display: block;
      margin-bottom: 2px;
      color: var(--sp-text);
      font-size: 11px;
      font-weight: 850;
    }

    .sp-overview-profile span {
      display: block;
      color: var(--sp-muted);
      font-size: 9px;
    }

    .sp-overview-list > div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 0;
      border-bottom: 1px solid #eef0f3;
    }

    .sp-overview-list span {
      color: var(--sp-muted);
      font-size: 9px;
    }

    .sp-overview-list strong {
      color: var(--sp-text);
      font-size: 10px;
      font-weight: 850;
      text-align: right;
      overflow-wrap: anywhere;
    }

    .sp-security-note {
      display: flex;
      align-items: flex-start;
      gap: 9px;
      margin-top: 15px;
      padding: 11px;
      border: 1px solid #d9f1e4;
      border-radius: 11px;
      background: #f4fbf7;
    }

    .sp-security-note > span {
      flex: 0 0 23px;
      width: 23px;
      height: 23px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: var(--sp-green);
      color: #fff;
      font-size: 9px;
      font-weight: 900;
    }

    .sp-security-note p {
      margin: 0;
      color: #567566;
      font-size: 8.5px;
      line-height: 1.6;
    }

    /* =====================================================
       AREA-BASED DATA LOADING
       Page layout remains visible while profile API loads.
    ===================================================== */

    .sp-data-skeleton {
      pointer-events: none;
    }

    .sp-skeleton-line,
    .sp-skeleton-box {
      display: block;
      position: relative;
      overflow: hidden;
      background: #eef0f4;
    }

    .sp-skeleton-line {
      border-radius: 999px;
    }

    .sp-skeleton-box {
      border-radius: 12px;
    }

    .sp-skeleton-line::after,
    .sp-skeleton-box::after {
      content: "";
      position: absolute;
      inset: 0;
      transform: translateX(-100%);
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.82),
        transparent
      );
      animation: sp-local-shimmer 1.25s infinite;
    }

    @keyframes sp-local-shimmer {
      100% {
        transform: translateX(100%);
      }
    }

    .sp-hero-data-skeleton {
      min-width: 280px;
    }

    .sp-profile-hero .sp-skeleton-line {
      background: rgba(255, 255, 255, 0.15);
    }

    .sp-profile-hero .sp-skeleton-line::after {
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.16),
        transparent
      );
    }

    .sp-sk-hero-name {
      width: 230px;
      max-width: 75vw;
      height: 31px;
      margin-bottom: 10px;
    }

    .sp-sk-hero-email {
      width: 190px;
      max-width: 65vw;
      height: 15px;
      margin-bottom: 15px;
    }

    .sp-sk-badge-row {
      display: flex;
      gap: 8px;
    }

    .sp-sk-badge {
      width: 125px;
      height: 35px;
      border-radius: 9px;
    }

    .sp-sk-stat-icon {
      width: 39px;
      height: 39px;
    }

    .sp-sk-chip {
      width: 60px;
      height: 22px;
    }

    .sp-sk-stat-value {
      width: 70%;
      height: 30px;
      margin-bottom: 10px;
    }

    .sp-sk-stat-title {
      width: 58%;
      height: 15px;
      margin-bottom: 8px;
    }

    .sp-sk-stat-note {
      width: 76%;
      height: 12px;
    }

    .sp-info-list-wrap {
      position: relative;
      min-height: 200px;
    }

    .sp-data-hidden {
      visibility: hidden;
    }

    .sp-panel-local-loading {
      position: absolute;
      inset: 0;
      z-index: 2;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 18px;
      background: #fff;
    }

    .sp-sk-info-row {
      display: flex;
      align-items: center;
      gap: 11px;
      min-width: 0;
      padding: 16px 2px;
      border-bottom: 1px solid #f0f1f4;
    }

    .sp-sk-info-icon {
      flex: 0 0 35px;
      width: 35px;
      height: 35px;
    }

    .sp-sk-info-row > div {
      flex: 1;
      min-width: 0;
    }

    .sp-sk-info-label {
      width: 85px;
      height: 12px;
      margin-bottom: 7px;
    }

    .sp-sk-info-value {
      width: min(180px, 80%);
      height: 16px;
    }

    .sp-overview-local-skeleton {
      padding-top: 1px;
    }

    .sp-sk-overview-profile {
      display: flex;
      align-items: center;
      gap: 11px;
      padding: 14px 0;
      border-bottom: 1px solid #eef0f3;
    }

    .sp-sk-avatar {
      flex: 0 0 42px;
      width: 42px;
      height: 42px;
    }

    .sp-sk-overview-profile > div {
      flex: 1;
    }

    .sp-sk-overview-name {
      width: 130px;
      height: 15px;
      margin-bottom: 7px;
    }

    .sp-sk-overview-ref {
      width: 85px;
      height: 12px;
    }

    .sp-sk-overview-list > div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 0;
      border-bottom: 1px solid #eef0f3;
    }

    .sp-sk-overview-label {
      width: 105px;
      height: 13px;
    }

    .sp-sk-overview-value {
      width: 82px;
      height: 14px;
    }

    /* LOADING / ERROR */

    .sp-loading-card,
    .sp-error-card {
      max-width: 500px;
      margin: 70px auto;
      padding: 38px 25px;
      border: 1px solid var(--sp-border);
      border-radius: 21px;
      background: #fff;
      text-align: center;
      box-shadow: 0 10px 35px rgba(30, 38, 64, 0.06);
    }

    .sp-spinner {
      width: 42px;
      height: 42px;
      margin: 0 auto 15px;
      border: 4px solid #e5e3ff;
      border-top-color: var(--sp-primary);
      border-radius: 50%;
      animation: sp-spin 0.75s linear infinite;
    }

    @keyframes sp-spin {
      to {
        transform: rotate(360deg);
      }
    }

    .sp-loading-card h3,
    .sp-error-card h3 {
      margin: 0 0 6px;
      color: var(--sp-text);
      font-size: 17px;
      font-weight: 850;
    }

    .sp-loading-card p,
    .sp-error-card p {
      margin: 0;
      color: var(--sp-muted);
      font-size: 10px;
      line-height: 1.6;
    }

    .sp-error-icon {
      width: 50px;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 14px;
      border-radius: 14px;
      background: #fff0ee;
      color: #f04438;
      font-size: 20px;
      font-weight: 900;
    }

    .sp-error-card button {
      margin-top: 16px;
      padding: 9px 17px;
      border: 0;
      border-radius: 10px;
      background: var(--sp-primary);
      color: #fff;
      font-size: 10px;
      font-weight: 800;
      cursor: pointer;
    }

    /* TABLET */

    @media (max-width: 1100px) {
      .sp-stat-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .sp-content-grid {
        grid-template-columns: 1fr;
      }

      .sp-overview-panel {
        position: static;
      }
    }

    /* MOBILE */

    @media (max-width: 650px) {
      .sp-panel-local-loading {
        grid-template-columns: 1fr;
      }

      .sp-sk-badge-row {
        flex-direction: column;
        align-items: flex-start;
      }

      .sp-sk-badge {
        width: 140px;
      }

      .sp-hero-data-skeleton {
        min-width: 0;
        width: 100%;
      }


      .sp-profile-hero {
        min-height: auto;
        flex-direction: column;
        align-items: flex-start;
        padding: 24px 18px;
        border-radius: 18px;
      }

      .sp-profile-main {
        width: 100%;
        align-items: flex-start;
      }

      .sp-avatar-wrap {
        flex-basis: 78px;
        width: 78px;
        height: 78px;
        border-radius: 21px;
      }

      .sp-avatar {
        border-radius: 17px;
      }

      .sp-profile-copy h1 {
        font-size: 20px;
      }

      .sp-profile-copy > p {
        max-width: 210px;
        overflow-wrap: anywhere;
      }

      .sp-profile-badges {
        flex-direction: column;
        align-items: flex-start;
      }

      .sp-account-status {
        width: 100%;
      }

      .sp-stat-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 9px;
      }

      .sp-stat-card {
        min-height: 145px;
        padding: 14px;
        border-radius: 15px;
      }

      .sp-stat-value {
        font-size: 17px;
      }

      .sp-info-list {
        grid-template-columns: 1fr;
      }

      .sp-panel {
        padding: 17px;
        border-radius: 17px;
      }
    }


/* =====================================================
   LARGE / NORMAL READABLE TEXT OVERRIDE
===================================================== */

/* Hero */
.sp-account-label {
  font-size: 13px;
}

.sp-profile-copy h1 {
  font-size: 30px;
}

.sp-profile-copy > p {
  font-size: 15px;
}

.sp-profile-badges span {
  font-size: 14px;
  padding: 9px 12px;
}

.sp-profile-badges small {
  font-size: 12px;
}

.sp-account-status small {
  font-size: 12px;
}

.sp-account-status strong {
  font-size: 15px;
}


/* =====================================================
   BALANCE CARDS
===================================================== */

.sp-stat-chip {
  font-size: 11px;
  padding: 5px 10px;
}

.sp-stat-value {
  font-size: 27px;
  line-height: 1.3;
}

.sp-stat-card h3 {
  font-size: 15px;
  line-height: 1.4;
}

.sp-stat-card p {
  font-size: 13px;
  line-height: 1.5;
}


/* =====================================================
   PANEL HEADINGS
===================================================== */

.sp-panel-kicker {
  font-size: 12px;
}

.sp-panel-header h2 {
  font-size: 21px;
}

.sp-panel-header p {
  font-size: 14px;
  line-height: 1.6;
}


/* =====================================================
   PERSONAL INFORMATION
===================================================== */

.sp-info-copy span {
  font-size: 13px;
  margin-bottom: 4px;
}

.sp-info-copy strong {
  font-size: 15px;
  line-height: 1.5;
}

.sp-info-icon {
  font-size: 14px;
}


/* =====================================================
   ACCOUNT OVERVIEW
===================================================== */

.sp-overview-profile strong {
  font-size: 15px;
}

.sp-overview-profile span {
  font-size: 13px;
}

.sp-overview-list span {
  font-size: 13px;
}

.sp-overview-list strong {
  font-size: 14px;
}


/* =====================================================
   SECURITY MESSAGE
===================================================== */

.sp-security-note p {
  font-size: 12px;
  line-height: 1.6;
}


/* =====================================================
   LOADING / ERROR
===================================================== */

.sp-loading-card h3,
.sp-error-card h3 {
  font-size: 21px;
}

.sp-loading-card p,
.sp-error-card p {
  font-size: 14px;
}

.sp-error-card button {
  font-size: 14px;
}


/* =====================================================
   MOBILE
===================================================== */

@media (max-width: 650px) {

  .sp-profile-copy h1 {
    font-size: 23px;
  }

  .sp-profile-copy > p {
    font-size: 14px;
  }

  .sp-profile-badges span {
    font-size: 13px;
  }

  .sp-profile-badges small {
    font-size: 11px;
  }

  .sp-account-status small {
    font-size: 11px;
  }

  .sp-account-status strong {
    font-size: 14px;
  }

  .sp-stat-value {
    font-size: 21px;
  }

  .sp-stat-card h3 {
    font-size: 14px;
  }

  .sp-stat-card p {
    font-size: 12px;
  }

  .sp-panel-header h2 {
    font-size: 19px;
  }

  .sp-panel-header p {
    font-size: 13px;
  }

  .sp-info-copy span {
    font-size: 12px;
  }

  .sp-info-copy strong {
    font-size: 14px;
  }

  .sp-overview-list span {
    font-size: 12px;
  }

  .sp-overview-list strong {
    font-size: 13px;
  }

  .sp-security-note p {
    font-size: 11px;
  }
}

    @media (max-width: 380px) {
      .sp-profile-main {
        flex-direction: column;
      }

      .sp-stat-grid {
        grid-template-columns: 1fr;
      }
    }
  `}</style>
);

export default AppProfile;
